import Label from '../../../sf-components/Label/Label';
import ButtonStyles from '../../../sf-components/Button/ButtonStyles';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, Icon, Icons, TextInput } from '@silverfort/sf-components';
import AlertsOptionsToggles from '../../containers/AlertsManagement/components/AlertsOptionsToggles';
import { isEmailValid } from '../../../utils';
import { EMAIL_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import ChipBank from '../../../sf-components/ChipBank/ChipBank';
import NotificationsEventDrawer from './NotificationsEventDrawer';
import { CUSTOMIZABLE_EVENTS } from './EmailConsts';
import { ALPHANUMERIC_REGEX } from '@silverfort-nodelibs/common/utils/consts';
import { SUBCATEGORIES } from '../../common/categories';

const { EMAIL } = SUBCATEGORIES;

const EmailDetails = ({ data, otherEmails, onSave, onCancel, isEditMode = false }) => {
    const [emailData, setEmailData] = useState(data);
    const [eventInEdit, setEventInEdit] = useState();
    const [nameError, setNameError] = useState();
    const [emailError, setEmailError] = useState();
    const [showCancelModal, setShowCancelModal] = useState(false);

    const validateName = (name) => {
        if (!name) return 'Mandatory field';
        if (Object.values(data).find((email) => email.name === name && email.id !== emailData.id))
            return 'Name already exists';
        if (!ALPHANUMERIC_REGEX.test(name)) return 'Use alphanumeric characters only';
    };

    const save = () => {
        const nameValidation = validateName(emailData.name);
        const emailValidation =
            emailData.emails.length === 0
                ? 'Mandatory field'
                : emailData.emails.map((email) => validateEmailInList(email)).filter((validation) => !!validation)?.[0];

        if (nameValidation || emailValidation) {
            setNameError(nameValidation);
            setEmailError(emailValidation);
        } else {
            setNameError();
            setEmailError();
            onSave({ ...emailData, lastModified: Date.now() });
        }
    };

    const validateEmailInput = (email) => {
        if (!isEmailValid(email)) return 'Invalid email address';
        if (emailData.emails.includes(email)) return 'Email already exists';
    };

    const validateEmailInList = (email) => {
        if (!isEmailValid(email)) return 'Invalid email addresses found';
        if (emailData.emails.filter((e) => e === email).length > 1) return 'Duplicate email addresses found';
    };

    const removeEmailAddress = (email, index) => {
        const emails = [...emailData.emails];
        emails.splice(index, 1);
        setEmailData({ ...emailData, emails });
        setEmailError();
    };

    const addEmailAddresses = (emailAddresses) => {
        setEmailData({ ...emailData, emails: [...emailData.emails, ...emailAddresses] });
        setEmailError();
    };

    const removeEmailAddresses = () => {
        setEmailData({ ...emailData, emails: [] });
        setEmailError();
    };

    const saveNotificationsSettings = (notificationSettings) => {
        const settings = { ...emailData.settings, [EMAIL_TYPES.NOTIFICATIONS.id]: notificationSettings };
        setEmailData({ ...emailData, settings });
    };

    const drawerDescription = (
        <div className="w-[512px] text-wrap">Get notified when a system event occurs. Add events by their ID.</div>
    );

    return (
        <div className="font-sf flex flex-col p-6 w-full h-92 border border-sf-gray-100 bg-white rounded-lg">
            <div className="mb-4 flex items-center bg-sf-gray-50 pl-6 h-20 border border-sf-gray-100 rounded-lg">
                <Icon className="text-sf-gray-400" icon={Icons.Mail} size={36} />
                <div className="ml-2 text-sm">
                    <div className="font-medium text-sf-gray-800">{EMAIL.NAME}</div>
                    <div className="font-normal text-sf-gray-500">
                        For more information about this integration and the required prerequisites, see
                        <a
                            className="ml-1 text-sf-primary-500"
                            href="https://docs.silverfort.com/docs/email-notifications"
                            target="_blank"
                            rel="noopener noreferrer">
                            here
                        </a>
                        .
                    </div>
                </div>
            </div>
            <div className={'flex flex-col gap-4'}>
                <div className="flex px-2">
                    <div className="w-1/5 flex h-10 items-center">
                        <Label text={'Name'} mandatory={true} />
                    </div>
                    <div className="w-4/5">
                        <TextInput
                            validationMessage={nameError}
                            placeholder={'Add name'}
                            value={emailData.name}
                            onChange={(val) => {
                                setNameError();
                                setEmailData({ ...emailData, name: val });
                            }}
                            className={'w-72'}
                        />
                    </div>
                </div>
                <div className="flex px-2">
                    <div className="w-1/5 flex h-10 items-center">
                        <Label text={'Recipients'} mandatory={true} />
                    </div>
                    <div className="w-4/5 flex flex-col">
                        <ChipBank
                            validateInput={validateEmailInput}
                            validateListItem={validateEmailInList}
                            addData={addEmailAddresses}
                            removeAllData={removeEmailAddresses}
                            removeData={removeEmailAddress}
                            data={emailData.emails}
                            placeholder={'Enter email addresses'}
                            icon={Icons.UserGroup}
                            inputClassName={'w-72'}
                            listClassName={'w-72 h-42'}
                        />
                        <span className="font-sf-primary text-10 font-normal text-sf-negative-500">{emailError}</span>
                    </div>
                </div>
                <div className="flex px-2">
                    <div className="w-1/5 flex h-10 items-center">
                        <Label text={'Events'} />
                    </div>
                    <div className="flex w-4/5 flex-col mt-0.5">
                        <AlertsOptionsToggles
                            data={emailData}
                            setData={setEmailData}
                            eventInEdit={eventInEdit}
                            setEventInEdit={setEventInEdit}
                            customizedEvents={CUSTOMIZABLE_EVENTS}
                            eventsTypes={EMAIL_TYPES}
                            title={'Platform Events'}
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end items-center h-8">
                <Button
                    text={'Cancel'}
                    buttonStyle={ButtonStyles.TertiarySolid}
                    onClick={() => (data !== emailData ? setShowCancelModal(true) : onCancel())}
                    className="mr-1"
                />
                <Button text={`Save${isEditMode ? ' Changes' : ''}`} disabled={data === emailData} onClick={save} />
            </div>
            <NotificationsEventDrawer
                data={emailData.settings[EMAIL_TYPES.NOTIFICATIONS.id]}
                open={eventInEdit}
                setOpen={setEventInEdit}
                onSave={saveNotificationsSettings}
                title={EMAIL_TYPES.NOTIFICATIONS.label}
                description={drawerDescription}
            />
            {showCancelModal && (
                <Modal
                    className="font-sf flex flex-col justify-center"
                    id={'cancelModal'}
                    dialogClassName="w-[360px] [&_.modal-content]:rounded-lg [&_.modal-content]:shadow-modal-light"
                    show
                    onHide={() => setShowCancelModal(false)}
                    keyboard={false}
                    backdrop="static">
                    <Modal.Header className="border-hidden pt-4 pr-6 pb-0 pl-0" closeButton>
                        <div className="px-4 pb-4 mb-1 text-14 font-medium text-sf-gray-800">Discard changes?</div>
                    </Modal.Header>
                    <Modal.Body className=" text-13 px-4 pb-6 h-full border-t border-b">
                        Any updates you made will be lost.
                    </Modal.Body>
                    <Modal.Footer className="border-hidden px-6">
                        <div className="flex justify-end items-center">
                            <Button
                                onClick={() => setShowCancelModal(false)}
                                text={'No'}
                                buttonStyle={ButtonStyles.TertiarySolid}
                                className="mr-2"
                            />
                            <Button onClick={onCancel} text={'Yes'} />
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default EmailDetails;
